function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import { PageHeader, Switcher } from "../../components/analytics-kit";
import account from "../../data/account";
import { hasTenantFeature, sellsThroughReps } from "../../functions/tenant-features";
import ServiceFactory from "../../service";

/*
 * Manage Data on the App UI kit (2026-09-29). One shell for every tab: the kit PageHeader
 * ("Manage Data" + one shared subtitle + the tab's actions) and the Switcher with the
 * section's views. Each tab keeps its own route (pagination and upload flows are route-based).
 */

export const TABS = [["/Data/Connect", "Connect"], ["/Data", "Feeds"], ["/Audience", "Audiences"], ["/Locations", "Markets"], ["/Product", "Products"]];
export const isAdmin = () => ((account.get() || {}).groups || []).some(g => ["owner", "administrator", "root"].includes(String(g).toLowerCase()));
export function visibleTabs() {
  const tabs = TABS.slice();
  const admin = isAdmin();
  if (admin && hasTenantFeature("productConfigs")) tabs.push(["/Data/Rules", "Configuration Rules"]);
  // Quotes desk tiers and the sales digests (SALES_QUOTES_ORDER_DESK.md §12, SALES_DIGESTS.md §9): owner and administrator.
  if (admin && hasTenantFeature("quotes") && sellsThroughReps()) tabs.push(["/Data/PriceTiers", "Price tiers"]);
  if (admin && (hasTenantFeature("salesTransactions") || hasTenantFeature("territoryRep") || hasTenantFeature("storePerformance"))) tabs.push(["/Data/Digests", "Sales digests"]);
  // Territories (SALES_TERRITORY_MAPS.md §5.1): ZIP to rep uploads with validation; owner/admin on rep-and-dealer workspaces.
  if (admin && hasTenantFeature("territoryRep") && sellsThroughReps()) tabs.push(["/Data/Territories", "Territories"]);
  if (admin) tabs.push(["/Data/Tracking", "Tracking"]);
  return tabs;
}
export function dataSvc(api, ep = "product") {
  const Service = ServiceFactory.getType(api, ep);
  return new Service();
}
export async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}

/** One-line notice on the kit token (`tone` = info | warning | danger). `onClose` adds a dismiss link. */
export function Notice({
  tone,
  children,
  onClose
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `wa-notice${tone && tone !== "info" ? " " + tone : ""}`
  }, children, onClose ? /*#__PURE__*/React.createElement("a", {
    className: "dismiss",
    onClick: onClose
  }, "dismiss") : null);
}

/** Kendo-styled buttons without the component (keeps the legacy pages' bundles small). */
export const Btn = ({
  primary,
  danger,
  small,
  children,
  ...rest
}) => /*#__PURE__*/React.createElement("button", _extends({}, rest, {
  className: `k-button k-button-${small ? "sm" : "md"} k-rounded-md k-button-solid k-button-solid-${primary ? "primary" : danger ? "error" : "base"}${rest.className ? " " + rest.className : ""}`
}), children);

/** One header line for every tab (dataflow mockups, 2026-10-09). */
export const SECTION_SUBTITLE = "Where your data comes from and how Plunk uses it";

/*
 * `subtitle` and `chips` are still accepted (tabs pass them) but no longer rendered: every tab
 * shows the same title and subtitle, and per-tab header chips are gone so the tabs look alike.
 * `actions` (a tab's own buttons) still render on the right.
 */
// eslint-disable-next-line no-unused-vars
export default function DataShell({
  active,
  subtitle,
  chips,
  actions,
  cls = "",
  children
}) {
  const items = visibleTabs().map(([to, label]) => ({
    to,
    label,
    exact: true,
    isActive: p => p === to || to !== "/Data" && p.startsWith(to + "/")
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: `territory-view wa-view data-view ${cls}`
  }, /*#__PURE__*/React.createElement(PageHeader, {
    title: "Manage Data",
    subtitle: SECTION_SUBTITLE,
    chips: [],
    actions: actions
  }), /*#__PURE__*/React.createElement(Switcher, {
    items: items,
    ariaLabel: "Manage Data views"
  }), children);
}